import { PAPER_A0, PAPER_A1, PAPER_A2, PAPER_A3, paperDimsMm } from "./paper.js";
import { POSTER_OVERLAP_MM, POSTER_PRINT_SHEET, POSTER_TRIM_MM } from "./poster.js";
import {
  ASSUMED_PRINTER_MARGIN_MM,
  type PageSheetMarks,
  sheetMarksFor,
} from "./sheet-mark-geometry.js";
import type { AssemblyInfo, PaperOrientation, PaperSize } from "./types.js";

export const POSTER_DIN_IDS = ["A3", "A2", "A1", "A0"] as const;
export type PosterDinId = (typeof POSTER_DIN_IDS)[number];
export const POSTER_DEFAULT_DIN: PosterDinId = "A2";
export const POSTER_MIN_WIDTH_CM = 30;
export const POSTER_MAX_LONG_SIDE_MM = 2000;
/** From this many sheets on the UI warns (no blocker). */
export const POSTER_LARGE_SHEET_COUNT = 30;
/** Images below this effective resolution get a quality warning. */
export const POSTER_LOW_DPI = 100;

const DIN_PAPERS: Record<PosterDinId, PaperSize> = {
  A3: PAPER_A3,
  A2: PAPER_A2,
  A1: PAPER_A1,
  A0: PAPER_A0,
};

/** Same tolerance as `tileGridForBoard`. */
const EPS = 1e-6;

export type PosterSizeChoice =
  | { kind: "din"; paperId: PosterDinId }
  | { kind: "custom"; widthCm: number };

export interface ResolvedPosterSize {
  wMm: number;
  hMm: number;
  posterLabel: string;
  fileSuffix: string;
  assemblyTitle?: string;
}

/** Strip float noise (560.0000000001 → 560) without hiding real fractions. */
function tidy(mm: number): number {
  return Math.round(mm * 1e6) / 1e6;
}

/**
 * DIN nominal size, file fitted inside: the format is oriented like the file,
 * the poster never exceeds it, and one side equals the nominal side.
 * `aspect` = width / height of the file.
 */
export function posterSizeForDin(paper: PaperSize, aspect: number): { wMm: number; hMm: number } {
  const long = paper.heightMm;
  const short = paper.widthMm;
  if (aspect >= 1) {
    const wMm = Math.min(long, short * aspect);
    return { wMm: tidy(wMm), hMm: tidy(wMm / aspect) };
  }
  const hMm = Math.min(long, short / aspect);
  return { wMm: tidy(hMm * aspect), hMm: tidy(hMm) };
}

export function customWidthRangeCm(aspect: number): { minCm: number; maxCm: number } | null {
  const maxCm = Math.floor(
    (aspect >= 1 ? POSTER_MAX_LONG_SIDE_MM : POSTER_MAX_LONG_SIDE_MM * aspect) / 10 + EPS,
  );
  return maxCm < POSTER_MIN_WIDTH_CM ? null : { minCm: POSTER_MIN_WIDTH_CM, maxCm };
}

export function posterSizeForWidth(widthCm: number, aspect: number): { wMm: number; hMm: number } {
  const wMm = widthCm * 10;
  return { wMm, hMm: tidy(wMm / aspect) };
}

export function resolvePosterSize(choice: PosterSizeChoice, aspect: number): ResolvedPosterSize {
  if (choice.kind === "din") {
    return {
      ...posterSizeForDin(DIN_PAPERS[choice.paperId], aspect),
      posterLabel: choice.paperId,
      fileSuffix: choice.paperId,
    };
  }
  return {
    ...posterSizeForWidth(choice.widthCm, aspect),
    posterLabel: `${choice.widthCm} cm`,
    fileSuffix: `${choice.widthCm}cm`,
    assemblyTitle: "Poster zusammensetzen",
  };
}

export function parseWidthCm(text: string, range: { minCm: number; maxCm: number }): number | null {
  const trimmed = text.trim().replace(",", ".");
  if (trimmed === "") return null;
  const value = Math.round(Number(trimmed));
  if (!Number.isFinite(value) || value < range.minCm || value > range.maxCm) return null;
  return value;
}

/** Fewest sheets n ≥ 1 with `n·sheet − (n−1)·overlap ≥ length`. */
export function gridCount(lengthMm: number, sheetMm: number, overlapMm: number): number {
  if (lengthMm <= sheetMm + EPS) return 1;
  return 1 + Math.ceil((lengthMm - sheetMm) / (sheetMm - overlapMm) - EPS);
}

export interface PosterGrid {
  cols: number;
  rows: number;
  sheetOrientation: PaperOrientation;
  sheetWMm: number;
  sheetHMm: number;
  overlapMm: number;
  gridWMm: number;
  gridHMm: number;
  offsetXMm: number;
  offsetYMm: number;
}

export interface PosterSheet {
  index: number;
  row: number;
  col: number;
  /** Top-left sheet corner in poster coordinates (poster starts at 0,0). */
  originXMm: number;
  originYMm: number;
  marks: PageSheetMarks | null;
}

export interface PosterPlan {
  posterWMm: number;
  posterHMm: number;
  trimMm: number;
  grid: PosterGrid;
  sheets: PosterSheet[];
}

/**
 * Sheet grid for a poster, centred. The grid covers the poster plus the
 * printer's unprintable margin on every side, so no part of the poster lands
 * where the printer can't reach. (Tactics posters tile via `posterTiling`.)
 */
export function planPosterGrid(args: {
  posterWMm: number;
  posterHMm: number;
  sheet: PaperSize;
  overlapMm: number;
}): PosterGrid {
  const { posterWMm, posterHMm, sheet, overlapMm } = args;
  const candidate = (sheetOrientation: PaperOrientation): PosterGrid => {
    const { wMm: sheetWMm, hMm: sheetHMm } = paperDimsMm(sheet, sheetOrientation);
    const marginMm = 2 * ASSUMED_PRINTER_MARGIN_MM;
    const cols = gridCount(posterWMm + marginMm, sheetWMm, overlapMm);
    const rows = gridCount(posterHMm + marginMm, sheetHMm, overlapMm);
    const gridWMm = cols * sheetWMm - (cols - 1) * overlapMm;
    const gridHMm = rows * sheetHMm - (rows - 1) * overlapMm;
    return {
      cols,
      rows,
      sheetOrientation,
      sheetWMm,
      sheetHMm,
      overlapMm,
      gridWMm,
      gridHMm,
      offsetXMm: (gridWMm - posterWMm) / 2,
      offsetYMm: (gridHMm - posterHMm) / 2,
    };
  };
  const portrait = candidate("portrait");
  const landscape = candidate("landscape");
  // Fewer sheets wins; a tie keeps portrait.
  return landscape.cols * landscape.rows < portrait.cols * portrait.rows ? landscape : portrait;
}

export function planPoster(args: {
  posterWMm: number;
  posterHMm: number;
  sheet?: PaperSize;
  overlapMm?: number;
  trimMm?: number;
}): PosterPlan {
  const overlapMm = args.overlapMm ?? POSTER_OVERLAP_MM;
  const trimMm = args.trimMm ?? POSTER_TRIM_MM;
  const grid = planPosterGrid({
    posterWMm: args.posterWMm,
    posterHMm: args.posterHMm,
    sheet: args.sheet ?? POSTER_PRINT_SHEET,
    overlapMm,
  });
  const multi = grid.cols * grid.rows > 1;
  const sheets: PosterSheet[] = [];
  for (let row = 0; row < grid.rows; row++) {
    for (let col = 0; col < grid.cols; col++) {
      sheets.push({
        index: row * grid.cols + col,
        row,
        col,
        originXMm: col * (grid.sheetWMm - overlapMm) - grid.offsetXMm,
        originYMm: row * (grid.sheetHMm - overlapMm) - grid.offsetYMm,
        marks: multi
          ? sheetMarksFor({ col, row, cols: grid.cols, rows: grid.rows, overlapMm, trimMm })
          : null,
      });
    }
  }
  return { posterWMm: args.posterWMm, posterHMm: args.posterHMm, trimMm, grid, sheets };
}

export function posterAssemblyInfo(
  plan: PosterPlan,
  size: Pick<ResolvedPosterSize, "posterLabel" | "assemblyTitle">,
): AssemblyInfo {
  return {
    cols: plan.grid.cols,
    rows: plan.grid.rows,
    overlapMm: plan.grid.overlapMm,
    trimMm: plan.trimMm,
    posterLabel: size.posterLabel,
    posterWMm: plan.posterWMm,
    posterHMm: plan.posterHMm,
    sheetWMm: plan.grid.sheetWMm,
    sheetHMm: plan.grid.sheetHMm,
    ...(size.assemblyTitle ? { title: size.assemblyTitle } : {}),
  };
}

export function effectiveDpi(pixelW: number, posterWMm: number): number {
  return pixelW / (posterWMm / 25.4);
}

export function formatCm(mm: number): string {
  return (Math.round(mm) / 10).toLocaleString("de-DE", { maximumFractionDigits: 1 });
}
